import React, { useState, useEffect } from "react";
import { Button, DatePicker, Select, Spin, notification, Empty, Tooltip, Input, Radio } from "antd";
import moment from "moment";
import { isEmpty, isUndefined, head, cloneDeep, forEach, isEqual, debounce } from "lodash";
import { CloseCircleOutlined } from "@ant-design/icons";

import TimingDropdown from "@components/time-dropdown";
import { CanEdit } from "@components/accessLevel";
import TextAreaComp from "@components/textArea";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import {
  dateFormatConfig,
  REMINDER_STATUS_LIST,
  REMINDER_SUCCESSFUL_VALUE,
  REMINDER_UNSUCCESSFUL_VALUE,
  MEMBER_DETAILS_CARE_MANAGEMENT_TASKS,
  CONTENT_ORIGIN_TYPE,
  REMINDER_NO_ACTION,
  EDIT,
  DELETE,
  ADD,
} from "@utils/constants";
import { formatDisplayDate, getClientName, getReminderMaxLength , getOrdinal} from "@utils/helpers";
import Icon from "@assets/icon_config";
import PatientProfileService from "@services/patientProfile.service";
import "./reminderForm.scss";
import {
  REMINDER_FORM_VIEW,
  REMINDER_RECURRENCE_VIEW,
  REMINDER_RECURRENCE_DAILY,
  REMINDER_RECURRENCE_WEEKLY,
  REMINDER_RECURRENCE_MONTHLY,
  REMINDER_RECURRENCE_END_BY,
  REMINDER_RECURRENCE_END_AFTER,
} from "../constants";
import ReminderRecurrence from "../reminderRecurrence/reminderRecurrence";
import DataNotFound from "@components/noDataFound";
import { NO_DATA_FOUND } from "../../../constants/constants";
import { DeleteAllRecurrence, EditAllRecurrence } from "./subComp";

const ReminderForm: any = ({
  action = ADD,
  listOfReminders = [],
  reminder = {},
  date,
  addReminder,
  editReminder,
  deleteReminder,
  closeReminderForm,
  showSearch,
  allowClear,
  reminderOrigin,
  disableRecurrence = false,
}: any) => {
  const [reminderData, setReminderData] = useState<any>({});
  const [fetchingPatientList, setFetchingPatientList] = useState<boolean>(false);
  const [membersOptions, setMembersOptions] = useState<any>([]);
  const [searchMembersInputString, setSearchMembersInputString] = useState<any>(null);
  const [selectedMembers, selectedMembersHandler] = useState<any>(null);
  const [refreshToTime, setRefreshToTime] = useState(true);
  const [reminderReadMode, setReminderReadMode] = useState(reminder?.reminderId ? false : true);
  const [viewAction, setViewAction] = useState(REMINDER_FORM_VIEW);
  const [deleteAllRecurrenceModalOpen, setDeleteAllRecurrenceModalOpen] = useState(false);
  const [editSeriesModalVisibility, setEditSeriesModalVisibility] = useState(false);
  const [shouldUpdateSeries, setShouldUpdateSeries] = useState(false);
  const [deleteSeriesModalVisibility, setDeleteSeriesModalVisibility] = useState(false);
  const [reminderSubmitLoader, setReminderSubmitLoader] = useState(false);
  const [reminderDeleteLoader, setReminderDeleteLoader] = useState(false);

  useEffect(() => {
    setReminderData({
      ...reminder,
      reminderStartDate: reminder?.reminderStartDate ? moment.utc(reminder?.reminderStartDate).local() : date ? moment(date).add(5, "minutes") : moment().add(5, "minutes"),
      reminderEndDate: reminder?.reminderEndDate ? moment.utc(reminder?.reminderEndDate).local() : date ? moment(date).add(20, "minutes") : moment().add(20, "minutes"),
    });
    selectedMembersHandler(isEmpty(reminder) ? null : reminder?.patientId);
    setMembersOptions(
      isEmpty(reminder)
        ? null
        : [
            {
              patient_id: reminder?.patientId,
              patient_name: reminder?.patientName,
            },
          ]
    );
  }, []);

  const getRecurStatement = () => {
    const startDate = moment(reminderData?.recurrence?.recurrenceCreatedDate).format("M/D/YYYY");
    const endDate = moment(reminderData?.recurrence?.range?.endDate).format("M/D/YYYY");
    const startTime = moment(reminderData?.reminderStartDate).format("hh:mm A");
    const endTime = moment(reminderData?.reminderEndDate).format("hh:mm A");
    const interval = reminderData?.recurrence?.frequency?.interval;

    if (reminderData?.recurrence?.type === REMINDER_RECURRENCE_DAILY) {
      return `Occurs every ${interval === 1 ? "day" : `${interval} days`} effective ${startDate} until ${endDate} from ${startTime} to ${endTime}.`;
    } else if (reminderData?.recurrence?.type === REMINDER_RECURRENCE_WEEKLY) {
      const week = reminderData?.recurrence?.frequency?.selection?.weekDay?.map((item: any) => moment().day(item).format("dddd"));
      return `Occurs every ${interval === 1 ? "" : `${interval} week(s) on`} ${
        week?.length > 1 ? week?.slice(0, -1).join(", ") + " and " + week?.slice(-1) : head(week)
      } effective ${startDate} until ${endDate} from ${startTime} to ${endTime}.`;
    } else if (reminderData?.recurrence?.type === REMINDER_RECURRENCE_MONTHLY) {
      const date = reminderData?.recurrence?.frequency?.selection?.monthDate;
      const monthWeek = reminderData?.recurrence?.frequency?.selection?.monthWeek;
      const day = reminderData?.recurrence?.frequency?.selection?.weekDay?.[0];

      return `Occurs ${
        date ? `day ${date} of every ${interval} month(s)` : `the ${getOrdinal(monthWeek)} ${moment().day(day).format("dddd")} of every ${interval} month(s)`
      } effective ${startDate} until ${endDate} from ${startTime} to ${endTime}.`;
    }
  };

  const recurStatement: any = getRecurStatement();

  const formatRecurStatement = () => {
    const dynamicRecurrenceLength = !reminderReadMode ? 50 : 42;
    if (recurStatement?.length > dynamicRecurrenceLength) {
      return (
        <Tooltip placement="bottom" title={recurStatement}>
          {recurStatement?.substring(0, dynamicRecurrenceLength)}...
        </Tooltip>
      );
    }
  };

  // To Fetch current TimeZone
  const getTimeZone = () => {
    return new Date().toLocaleDateString(undefined, { day: "2-digit", timeZoneName: "long" }).substring(4);
  };

  // Search members to add reminder
  const debounceMemberSearch = debounce((inputString) => getMembersList(inputString), 500);

  const getMembersList = (inputString: string) => {
    const searchPatient = inputString?.trim();
    setSearchMembersInputString(searchPatient);
    if (searchPatient.length > 2) {
      setFetchingPatientList(true);
      PatientProfileService.fetchPatientsList({ patient_name: searchPatient }).subscribe({
        next: (v) => {
          let response = v?.data?.data;
          setMembersOptions(response);
          setFetchingPatientList(false);
        },
        error: (e) => {
          console.log(e);
          setFetchingPatientList(false);
        },
      });
    } else {
      setMembersOptions([]);
    }
  };

  const getCalFormattedDate = (current: any) => {
    let showDot = false;
    const dateKey = current.startOf("day").unix() + "-" + current.endOf("day").unix();
    const filtered = listOfReminders.filter(
      (rem: any) =>
        `${moment(formatDisplayDate(rem.reminderStartDate, "reminderDateActualFormat", "reminderDateActualFormat", true)).startOf("day").unix()}-${moment(
          formatDisplayDate(rem.reminderStartDate, "reminderDateActualFormat", "reminderDateActualFormat", true)
        )
          .endOf("day")
          .unix()}` === dateKey
    );
    if (filtered && filtered.length > 0) {
      showDot = true;
    }

    return (
      <div className="ant-picker-cell-inner">
        {showDot && <span className="dateDot"></span>}
        {current.date()}
      </div>
    );
  };

  const membersChangeHandler = (value: any, option: any) => {
    setReminderData({
      ...reminderData,
      patientId: value,
      patientName: option?.item?.patient_name,
    });
    selectedMembersHandler(value);

    if (value === "" || value === undefined || value === null) {
      setMembersOptions([]);
    }
  };

  const saveReminder = () => {
    const _from = moment(moment(reminderData?.reminderStartDate).format("HH:mm"), "HH:mm");
    const _to = moment(moment(reminderData?.reminderEndDate).format("HH:mm"), "HH:mm");

    if (_to <= _from || isUndefined(reminderData?.reminderStartDate) || isUndefined(reminderData?.reminderEndDate)) {
      notification.error({
        message: "Error",
        description: isUndefined(reminderData?.reminderStartDate) || isUndefined(reminderData?.reminderEndDate) ? "Please fill time." : "Please enter proper time range.",
      });
    } else if (moment(reminderData?.reminderStartDate).add(1, "m") < moment() && !reminderData?.reminderId) {
      notification.error({
        message: "Error",
        description: "Reminders for past time cannot be added. Please enter proper time range.",
      });
    }
    else if ((isEmpty(selectedMembers?.toString()) || isUndefined(selectedMembers?.toString())) && isEmpty(reminderData?.reminderTitle)) {
      notification.error({
        message: "Error",
        description: "Please select Members or Add Title.",
      });
    } else if (isEmpty(reminderData?.reminder) || isUndefined(reminderData?.reminder)) {
      notification.error({
        message: "Error",
        description: "Please add content.",
      });
    } else if (reminderData?.reminderId) {
      setReminderSubmitLoader(true);
      if (!isEqual(reminder?.recurrence, reminderData?.recurrence) || shouldUpdateSeries) {
        const recurrence = !isEqual(reminder?.recurrence, reminderData?.recurrence)
          ? {
              ...cloneDeep(reminderData?.recurrence),
              startTime: moment(reminderData?.reminderStartDate).format("HH:mm"),
              endTime: moment(reminderData?.reminderEndDate).format("HH:mm"),
            }
          : null;
        const checkForTimeStampChange = moment.utc(reminder?.reminderStartDate).local().format("YYYY-MM-DD HH:mm") !== reminderData?.reminderStartDate.format("YYYY-MM-DD HH:mm") || moment.utc(reminder?.reminderEndDate).local().format("YYYY-MM-DD HH:mm") !== reminderData?.reminderEndDate.format("YYYY-MM-DD HH:mm");

        editReminder(
          {
            ...(reminder?.reminderStatus !== reminderData?.reminderStatus && { reminderStatus: reminderData?.reminderStatus }),
            ...(reminder?.reminder !== reminderData?.reminder && { reminder: reminderData?.reminder }),
            recurrence: recurrence,
            ...(recurrence ? {reminderEndDate: reminderData?.reminderEndDate, reminderStartDate: reminderData?.reminderStartDate} : {
              ...(checkForTimeStampChange ? {
                reminderStartDate: reminderData?.reminderStartDate,
                reminderEndDate: reminderData?.reminderEndDate,
                recurrence: (function(){
                  let tempRecur = recurrence || reminderData?.recurrence;
                  tempRecur = {
                    ...tempRecur,
                    startTime: moment(reminderData?.reminderStartDate).format("HH:mm"),
                    endTime: moment(reminderData?.reminderEndDate).format("HH:mm"),
                  }
                  return tempRecur
                })()
              } : {})
            }),
            reminder: reminderData?.reminder,
            prevReminderStartDate: reminder?.reminderStartDate,
            parentReminderId: reminder?.parentReminderId,
            reminderTitle: reminderData?.reminderTitle,
            editRecurrenceFlag: shouldUpdateSeries,
            reminderId: reminderData?.reminderId,
            patientId: reminderData?.patientId,
            patientName: reminderData?.patientName,
            timeZoneOffset: moment().utcOffset() * -1,
          },
          () => setReminderSubmitLoader(false)
        );
      } else {
        editReminder(
          {
            reminderId: reminderData?.reminderId,
            patientId: reminderData?.patientId,
            patientName: reminderData?.patientName,
            ...(reminder?.reminderStatus !== reminderData?.reminderStatus && { reminderStatus: reminderData?.reminderStatus }),
            ...(reminder?.reminder !== reminderData?.reminder && { reminder: reminderData?.reminder }),
            ...(moment.utc(reminder?.reminderStartDate).local().format("YYYY-MM-DD HH:mm") !== reminderData?.reminderStartDate.format("YYYY-MM-DD HH:mm") && {
              reminderStartDate: reminderData?.reminderStartDate,
            }),
            ...(moment.utc(reminder?.reminderEndDate).local().format("YYYY-MM-DD HH:mm") !== reminderData?.reminderEndDate.format("YYYY-MM-DD HH:mm") && {
              reminderEndDate: reminderData?.reminderEndDate,
            }),
            reminderTitle: reminderData?.reminderTitle,
            timeZoneOffset: moment().utcOffset() * -1,
          },
          () => setReminderSubmitLoader(false)
        );
      }
    } else {
      setReminderSubmitLoader(true);
      const recurrence = reminderData?.recurrence
        ? {
            ...cloneDeep(reminderData?.recurrence),
            startTime: moment(reminderData?.reminderStartDate).format("HH:mm"),
            endTime: moment(reminderData?.reminderEndDate).format("HH:mm"),
            timeZoneOffset: moment().utcOffset() * -1,
          }
        : null;
      addReminder(
        {
          recurrence: recurrence,
          patientId: reminderData?.patientId,
          reminderType: reminderOrigin,
          description: reminderData?.reminder,
          reminderStartDate: reminderData?.reminderStartDate,
          reminderEndDate: reminderData?.reminderEndDate,
          patientName: reminderData?.patientName,
          reminderTitle: reminderData?.reminderTitle,
          timeZoneOffset: moment().utcOffset() * -1,
        },
        () => setReminderSubmitLoader(false)
      );
    }
  };

  const onReminderStatusChange = (value: any) => {
    setReminderData({
      ...cloneDeep(reminderData),
      reminderStatus: value,
    });
  };

  const clearRecurrenceHandler = () => {
    if (reminder?.recurrence) {
      setDeleteAllRecurrenceModalOpen(true);
    } else {
      clearRecurrence();
    }
  };

  const clearRecurrence = () => {
    setReminderData({
      ...cloneDeep(reminderData),
      reminderStartDate: reminder?.reminderStartDate ? moment.utc(reminder?.reminderStartDate).local() : moment().add(5, "minutes"),
      reminderEndDate: reminder?.reminderEndDate ? moment.utc(reminder?.reminderEndDate).local() : moment().add(15, "minutes"),
      recurrence: null,
    });
    setDeleteAllRecurrenceModalOpen(false);
  };

  const computeDateForReminderRecurrenceWeeklyEndBy = (startDate: any) => {
    const selectedDays = reminderData.recurrence?.frequency.selection.weekDay;
    const occurrence = reminderData.recurrence.range.occurrence;
    const interval = reminderData.recurrence.frequency.interval;
    let temp_startDate = moment(cloneDeep(startDate));
    let updatedReminderStartDate = moment(cloneDeep(startDate));

    let occurrenceCounter = 0;
    while (occurrenceCounter < occurrence) {
      // eslint-disable-next-line no-loop-func
      forEach(selectedDays, (day: any) => {
        if (temp_startDate.weekday() <= day) {
          if (occurrenceCounter < occurrence) {
            temp_startDate.isoWeekday(day);
            console.log(occurrenceCounter, temp_startDate.format("YYYY-MM-DD"), temp_startDate.date(), day, temp_startDate.date() >= day);
          }
          occurrenceCounter += 1;
          if (occurrenceCounter === 1) {
            updatedReminderStartDate = cloneDeep(temp_startDate);
          }
        }
      });
      if (occurrenceCounter < occurrence) {
        temp_startDate.add(interval, "weeks").startOf("isoWeek");
      }
    }
    return { endDate: temp_startDate, startDate: updatedReminderStartDate };
  };

  const computeDateForReminderRecurrenceMonthlyEndBy = (startDate: any) => {
    const selectedDate: any = reminderData.recurrence?.frequency?.selection?.monthDate;
    const selectedWeek: any = reminderData.recurrence?.frequency?.selection?.monthWeek;
    const selectedWeekDay: any = reminderData.recurrence?.frequency?.selection?.weekDay?.[0];
    const interval = reminderData.recurrence?.frequency?.interval;
    const occurrence = reminderData.recurrence.range.occurrence;

    let occurrenceCounter = 0;
    let temp_startDate = moment(cloneDeep(startDate));
    let updatedReminderStartDate = moment(cloneDeep(startDate));
    const previousSelectedReminderStartDate = moment(cloneDeep(reminderData.reminderStartDate));

    if (selectedDate) {
      while (occurrenceCounter < occurrence) {
        if (moment(moment().format("YYYY-MM-DD")) <= moment(temp_startDate.format("YYYY-MM-DD"))) {
          occurrenceCounter += 1;
          temp_startDate.add(1, "months").date(selectedDate);
          if (occurrenceCounter === 1) {
            updatedReminderStartDate = cloneDeep(temp_startDate);
          }
        } else if (moment() > temp_startDate.date(selectedDate)) {
          temp_startDate.add(1, "months").date(selectedDate);
          occurrenceCounter += 1;
          if (occurrenceCounter === 1) {
            updatedReminderStartDate = cloneDeep(temp_startDate);
          }
        } else {
          temp_startDate.date(selectedDate);
          occurrenceCounter += 1;
          if (occurrenceCounter === 1) {
            updatedReminderStartDate = cloneDeep(temp_startDate);
            temp_startDate.add(1, "months");
          } else if (occurrenceCounter < occurrence) {
            temp_startDate.add(interval, "months");
          }
        }
      }
    } else if (selectedWeek && selectedWeekDay !== null) {
      if (temp_startDate.month() === previousSelectedReminderStartDate.month()) {
        temp_startDate.add(1, "months");
      }
      while (occurrenceCounter < occurrence) {
        temp_startDate.startOf("month");
        if (temp_startDate.day() <= selectedWeekDay) {
          temp_startDate.day(selectedWeekDay);
          temp_startDate.add((selectedWeek - 1) * 7, "days");
        } else if (temp_startDate.day() > selectedWeekDay) {
          temp_startDate.add(selectedWeek * 7, "days");
          temp_startDate.startOf("week");
          temp_startDate.day(selectedWeekDay);
        }
        if (moment(temp_startDate.format("YYYY-MM-DD")) < moment(moment().format("YYYY-MM-DD"))) {
          temp_startDate.add(1, "months");
          temp_startDate.startOf("month");
          if (temp_startDate.day() <= selectedWeekDay) {
            temp_startDate.day(selectedWeekDay);
            temp_startDate.add((selectedWeek - 1) * 7, "days");
          } else if (temp_startDate.day() > selectedWeekDay) {
            temp_startDate.add(selectedWeek * 7, "days");
            temp_startDate.startOf("week");
            temp_startDate.day(selectedWeekDay);
          }
        }
        occurrenceCounter += 1;
        if (occurrenceCounter === 1) {
          updatedReminderStartDate = cloneDeep(temp_startDate);
          console.log(temp_startDate.format("YYYY-MM-DD"), occurrenceCounter);
          if (occurrence > 1) {
            temp_startDate.add(interval, "months");
          }
        } else if (occurrence > 1 && occurrenceCounter < occurrence) {
          console.log(temp_startDate.format("YYYY-MM-DD"), occurrenceCounter);
          temp_startDate.add(interval, "months");
        }
      }
    }
    return { endDate: temp_startDate, startDate: updatedReminderStartDate };
  };

  const onReminderStartDateChange = (value: any) => {
    let reminderOnChange = {
      ...cloneDeep(reminderData),
      reminderStartDate: moment(`${moment(value).format("YYYY-MM-DD")} ${moment(reminderData?.reminderStartDate).format("HH:mm")}`),
      reminderEndDate: moment(`${moment(value).format("YYYY-MM-DD")} ${moment(reminderData?.reminderEndDate).format("HH:mm")}`),
    };

    if (!isEqual(reminder?.recurrence, reminderData?.recurrence)) {
      if (reminderOnChange.recurrence) {
        const reminderRecurrenceEndType = reminderOnChange.recurrence.range.type;
        switch (reminderRecurrenceEndType) {
          case REMINDER_RECURRENCE_END_BY:
            reminderOnChange.recurrence.range.endDate =
              moment(moment(reminderOnChange.recurrence.range.endDate).format("YYYY-MM-DD")) > moment(reminderOnChange?.reminderStartDate.format("YYYY-MM-DD"))
                ? reminderOnChange.recurrence.range.endDate.format("YYYY-MM-DD")
                : reminderOnChange?.reminderStartDate;
            break;
          case REMINDER_RECURRENCE_END_AFTER:
            const interval = reminderOnChange.recurrence?.frequency?.interval;
            const occurrence = reminderOnChange.recurrence.range.occurrence;

            switch (reminderOnChange.recurrence?.type) {
              case REMINDER_RECURRENCE_DAILY:
                reminderOnChange.recurrence.range.endDate = moment(reminderOnChange?.reminderStartDate)
                  .add((occurrence - 1) * interval, "days")
                  .format("YYYY-MM-DD");
                break;
              case REMINDER_RECURRENCE_WEEKLY: {
                const boundaryDates = computeDateForReminderRecurrenceWeeklyEndBy(reminderOnChange?.reminderStartDate);
                reminderOnChange.recurrence.range.endDate = boundaryDates?.endDate.format("YYYY-MM-DD");
                reminderOnChange.reminderStartDate = moment(`${moment(boundaryDates?.startDate).format("YYYY-MM-DD")} ${reminderOnChange?.reminderStartDate?.format("HH:mm")}`);
                reminderOnChange.reminderEndDate = moment(`${moment(boundaryDates?.startDate).format("YYYY-MM-DD")} ${reminderOnChange?.reminderEndDate?.format("HH:mm")}`);

                break;
              }
              case REMINDER_RECURRENCE_MONTHLY:
                const boundaryDates = computeDateForReminderRecurrenceMonthlyEndBy(reminderOnChange?.reminderStartDate);
                reminderOnChange.recurrence.range.endDate = boundaryDates?.endDate.format("YYYY-MM-DD");
                reminderOnChange.reminderStartDate = moment(`${moment(boundaryDates?.startDate).format("YYYY-MM-DD")} ${reminderOnChange?.reminderStartDate?.format("HH:mm")}`);
                reminderOnChange.reminderEndDate = moment(`${moment(boundaryDates?.startDate).format("YYYY-MM-DD")} ${reminderOnChange?.reminderEndDate?.format("HH:mm")}`);
                break;
              default:
                break;
            }
        }
        reminderOnChange.recurrence.recurrenceCreatedDate = reminderOnChange.reminderStartDate;
      }
    }
    setReminderData(reminderOnChange);
  };

  const disableReminderSubmitButton = () => {
    const disable =
      !reminderReadMode && moment(reminderData?.reminderStartDate) < moment() && reminder?.reminderStatus !== null
        ? true
        : !reminderReadMode && moment(reminderData?.reminderStartDate) < moment() && reminder?.reminderStatus === null
        ? false
        : reminderReadMode
        ? false
        : true;

    return reminderData?.reminder?.length > getReminderMaxLength() ? true : disable;
  };

  // This disable code need to be made generic
  const disableEditBasedOnOriginOfReminders = () => {
    if (reminderData?.reminderType === MEMBER_DETAILS_CARE_MANAGEMENT_TASKS) {
      return reminderOrigin === MEMBER_DETAILS_CARE_MANAGEMENT_TASKS;
    }
    return true;
  };

  const disable_DateTimeStamp_While_Creating_NewReminder = () => {
    return reminder?.recurrence ? !isEqual(reminderData?.recurrence, reminder?.recurrence) : reminderData?.recurrence ? true : false;
  };

  const getReminderFormDOM = () => {
    return (
      <>
        {deleteAllRecurrenceModalOpen && <DeleteAllRecurrence {...{ clearRecurrence, setDeleteAllRecurrenceModalOpen }} />}
        {editSeriesModalVisibility && !isEmpty(reminderData?.recurrence) && (
          <EditAllRecurrence
            metaDataKey={EDIT}
            toggleModal={setEditSeriesModalVisibility}
            showModal={editSeriesModalVisibility}
            readMode={reminderReadMode}
            radioButtonVal={shouldUpdateSeries}
            setReadMode={setReminderReadMode}
            closeModal={() => {
              setShouldUpdateSeries(false);
              setEditSeriesModalVisibility(!editSeriesModalVisibility);
            }}
            setModalData={(data: any) => setShouldUpdateSeries(data)}
            onOk={()=>setReminderReadMode(!reminderReadMode)}
          />
        )}
        {deleteSeriesModalVisibility &&
          <EditAllRecurrence metaDataKey={DELETE} toggleModal={setDeleteSeriesModalVisibility} onOk={({operationRange} : {operationRange : string}) => {
            setReminderDeleteLoader(true);
            deleteReminder(reminderData , () => setReminderDeleteLoader(false) , operationRange);
          }}/>
        }
        <div className="reminderForm__reminderContainer">
          <div className="reminderForm__reminderContainer__header">
            <div className="reminderForm__reminderContainer__header__title">
              <div className="reminderForm__reminderContainer__header__title__reminder-type">{reminder?.reminderId ? "Edit Reminder" : "Add Reminder"}</div>
              <div className="reminderForm__reminderContainer__header__title__time-zone">( Time Zone : {getTimeZone()} )</div>
            </div>
            <div className="reminderForm__reminderContainer__header__actions">
              <CanEdit>
                {!reminderReadMode &&
                  disableEditBasedOnOriginOfReminders() &&
                  /*according to the requirement user should be able to reschedule the past reminders , so the below line is commented out
                   moment(reminderData?.reminderStartDate).isSameOrAfter(moment()) &&*/
                  ![REMINDER_SUCCESSFUL_VALUE, REMINDER_UNSUCCESSFUL_VALUE].includes(reminder?.reminderStatus) && (
                    <>
                      <div className="reminderForm__reminderContainer__header__actions__edit">
                        <i
                          className="icon-edit f18 clr-blueLink pointer"
                          onClick={() => {
                            if (reminder?.reminderId && reminderData?.recurrence) {
                              setEditSeriesModalVisibility(!editSeriesModalVisibility);
                            } else {
                              setReminderReadMode(!reminderReadMode);
                            }
                          }}
                        />
                      </div>
                      <div className="reminderForm__reminderContainer__header__actions__delete">
                        <Spin spinning={reminderDeleteLoader} indicator={<HealthemSpinner />}>
                          <i
                            className="icon-delete f18 clr-e100 pointer"
                            onClick={() => {
                              if(reminderData?.parentReminderId) {
                                setDeleteSeriesModalVisibility(true);
                              } else {
                                setReminderDeleteLoader(true);
                                deleteReminder(reminderData, () => setReminderDeleteLoader(false));
                              }
                            }}
                          />
                        </Spin>
                      </div>
                    </>
                  )}
              </CanEdit>
              {!disableEditBasedOnOriginOfReminders() && <div className="reminderForm__reminderContainer__header__actions__reminder-type-badge">{CONTENT_ORIGIN_TYPE[reminderData?.reminderType]}</div>}
            </div>
          </div>
          <div className="reminderForm__reminderContainer__body">
            <div className="reminderForm__reminderContainer__body__section-1">
              <div className="icon-clock" />
              <div className="reminderForm__reminderContainer__body__section-1__date-selector">
                <DatePicker
                  value={moment(reminderData?.reminderStartDate)}
                  onChange={(val) => onReminderStartDateChange(val)}
                  disabledDate={(current) => action === ADD ? moment(current.format("YYYY-MM-DD")) < moment(moment().format("YYYY-MM-DD")) : false}
                  disabled={!reminderReadMode || disable_DateTimeStamp_While_Creating_NewReminder()}
                  size={"middle"}
                  format={dateFormatConfig.get(getClientName())["defaultDateFormat"]}
                  clearIcon={false}
                  dateRender={getCalFormattedDate}
                  dropdownClassName="reminderForm__reminderContainer__body__section-1__date-selector__date-picker"
                  // className="ui-add-reminder__calWrap__reminderCal"
                />
              </div>
              <div className="reminderForm__reminderContainer__body__section-1__time-selector">
                <TimingDropdown
                  disabled={!reminderReadMode || disable_DateTimeStamp_While_Creating_NewReminder()}
                  value={moment(reminderData?.reminderStartDate).format("HH:mm")}
                  onChange={(time: any) => {
                    setReminderData({
                      ...reminderData,
                      reminderStartDate: moment(`${moment(reminderData?.reminderStartDate).format("YYYY-MM-DD")} ${time}`),
                      reminderEndDate: moment(`${moment(reminderData?.reminderStartDate).format("YYYY-MM-DD")} ${moment(time, "HH:mm").add(15, "m").format("HH:mm")}`),
                    });
                    setRefreshToTime(!refreshToTime);
                  }}
                  // timeslot={true}
                  date={moment(reminderData?.reminderStartDate).format("YYYY-MM-DD")}
                  // isToDate={false}
                  // refreshToTime={true}
                />
                <label className="reminderForm__reminderContainer__body__section-1__time-selector__time-separator">to</label>
                <TimingDropdown
                  disabled={!reminderReadMode || disable_DateTimeStamp_While_Creating_NewReminder()}
                  value={moment(reminderData?.reminderEndDate).format("HH:mm")}
                  onChange={(time: any) =>
                    setReminderData({
                      ...reminderData,
                      reminderEndDate: moment(`${moment(reminderData?.reminderEndDate).format("YYYY-MM-DD")} ${time}`),
                    })
                  }
                  date={moment(reminderData?.reminderEndDate).format("YYYY-MM-DD")}
                  isToDate={true}
                  fromTime={moment(reminderData?.reminderStartDate).format("HH:mm")}
                  // refreshToTime={refreshToTime}
                />
              </div>
            </div>
            {!disableRecurrence && (!reminderReadMode ? true : !reminder?.reminderId ? true : shouldUpdateSeries ? true : isEmpty(reminderData?.recurrence) ? true : false) && (
              <div className="reminderForm__reminderContainer__body__section-2">
                <div className="reminderForm__reminderContainer__body__section-2__recur-icon">
                  <Icon name={"refresh"} />
                </div>
                <div className={`reminderForm__reminderContainer__body__section-2__recur-value`}>
                  {isEmpty(reminderData?.recurrence) ? (
                    <div
                      className={`reminderForm__reminderContainer__body__section-2__recur-value__create-reoccurrence ${!reminderReadMode ? " disable-create-recurrence" : ""}`}
                      onClick={() => setViewAction(REMINDER_RECURRENCE_VIEW)}
                    >
                      Make Recurring
                    </div>
                  ) : (
                    // If recurrence is added we will disable the edit recurrence logic for current development phase
                    <>
                      <div className="reminderForm__reminderContainer__body__section-2__recur-value__statement">{formatRecurStatement()}</div>
                      {reminderReadMode && (
                        <>
                          <div className="reminderForm__reminderContainer__body__section-2__recur-value__edit-button" onClick={() => setViewAction(REMINDER_RECURRENCE_VIEW)}>
                            Edit
                          </div>
                          <div className="reminderForm__reminderContainer__body__section-2__recur-value__clear-recurrence">
                            <Tooltip placement="top" title={"Remove Recurrence"}>
                              <CloseCircleOutlined style={{ color: "red" }} onClick={() => clearRecurrenceHandler()} />
                            </Tooltip>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="reminderForm__reminderContainer__body__section-3">
              <div className="icon-users" />
              <div className="reminderForm__reminderContainer__body__section-3__search-members">
                <Select
                  placeholder="Search and Add Members"
                  value={selectedMembers ? selectedMembers : undefined}
                  onChange={(value: any, option: any) => membersChangeHandler(value, option)}
                  onSearch={(value: string) => debounceMemberSearch(value)}
                  // style={{ width: "32.4vw" }}
                  bordered={false}
                  showArrow={true}
                  maxTagCount={2}
                  showSearch={showSearch}
                  allowClear={allowClear}
                  // open={true}
                  notFoundContent={
                    fetchingPatientList ? (
                      <Spin size="small" indicator={<HealthemSpinner />} />
                    ) : membersOptions?.length > 0 ? null : searchMembersInputString?.length > 3 ? (
                      <DataNotFound message={NO_DATA_FOUND} />
                    ) : null
                  }
                  filterOption={false}
                  disabled={!showSearch || !reminderReadMode}
                >
                  {membersOptions?.map((item: any, index: number) => (
                    <Select.Option key={item.patient_id} value={item.patient_id} item={item}>
                      <div className="reminderForm__reminderContainer__body__section-3__search-members__options">
                        <div className="reminderForm__reminderContainer__body__section-3__search-members__options__avatar-container">
                          {/* <Avatar
                            className="reminderForm__reminderContainer__body__section-3__search-members__options__avatar-container__avatar"
                            style={{
                              background: index > colors.length - 1 ? colors[0] : colors[index],
                            }}
                          >
                            {item?.patient_name?.charAt(0)}
                          </Avatar> */}
                          <div className="reminderForm__reminderContainer__body__section-3__search-members__options__avatar-container__name">{item?.patient_name}</div>
                        </div>
                        {item?.patient_dob ? (
                          <div className="reminderForm__reminderContainer__body__section-3__search-members__options__dob">
                            {formatDisplayDate(item?.patient_dob, "createDateFormat", "defaultDateFormat")}
                          </div>
                        ) : null}
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="reminderForm__reminderContainer__body__section-title">
              <Icon name="title" />
              <div className="reminderForm__reminderContainer__body__section-title__search-members">
                <Input
                  placeholder="Enter Title"
                  value={reminderData?.reminderTitle}
                  disabled={!reminderReadMode}
                  onChange={(e) =>
                    setReminderData({
                      ...reminderData,
                      reminderTitle: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="reminderForm__reminderContainer__body__section-4" aria-disabled={false}>
              <TextAreaComp
                actionsMenu={false}
                content={reminderData?.reminder}
                disabled={!reminderReadMode}
                changeHandler={(reminderText: string) =>
                  setReminderData({
                    ...reminderData,
                    reminder: reminderText?.trim(),
                  })
                }
                showCount={reminderReadMode}
                wordLimit={getReminderMaxLength()}
              />
            </div>
            <CanEdit>
              <div className="reminderForm__reminderContainer__body__section-5" aria-disabled={false}>
                <div className="reminderForm__reminderContainer__body__section-5__reminder-status">
                  <div className="reminderForm__reminderContainer__body__section-5__reminder-status__label">Reminder Status</div>
                  <div className="reminderForm__reminderContainer__body__section-5__reminder-status__value">
                    <Select
                      value={reminderData?.reminderStatus}
                      placeholder="Select"
                      disabled={
                        reminderData?.reminderId
                          ? moment() < moment(reminderData?.reminderStartDate)
                            ? !reminderReadMode
                            : false || reminderReadMode || [REMINDER_SUCCESSFUL_VALUE, REMINDER_UNSUCCESSFUL_VALUE].includes(reminder?.reminderStatus)
                          : true
                      }
                      onChange={(status: any) => onReminderStatusChange(status)}
                    >
                      {REMINDER_STATUS_LIST?.map(
                        (item: any) =>
                          item?.value !== REMINDER_NO_ACTION && (
                            <Select.Option key={item?.label} value={item?.value} item={item}>
                              {item?.label}
                            </Select.Option>
                          )
                      )}
                    </Select>
                  </div>
                </div>
                {reminderData?.reminderCreatedBy && (
                  <div className="reminderForm__reminderContainer__body__section-5__reminder-created-by">
                    <div className="reminderForm__reminderContainer__body__section-5__reminder-created-by__label">Created By</div>
                    {/* `${session?.user?.lastName}, ${session?.user?.firstName}` */}
                    <div className="reminderForm__reminderContainer__body__section-5__reminder-created-by__value">{reminderData?.reminderCreatedBy}</div>
                  </div>
                )}
              </div>
              <div className="reminderForm__reminderContainer__body__footer">
                <Button type="text" size="large" className="f16 w600 reminderForm__reminderContainer__body__footer__cancel-btn" onClick={closeReminderForm}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="large"
                  className="f16 w600 reminderForm__reminderContainer__body__footer__submit-btn"
                  onClick={saveReminder}
                  disabled={disableReminderSubmitButton()}
                  loading={reminderSubmitLoader}
                >
                  Save
                </Button>
              </div>
            </CanEdit>
          </div>
        </div>
      </>
    );
  };

  switch (viewAction) {
    case REMINDER_FORM_VIEW:
      return getReminderFormDOM();
    case REMINDER_RECURRENCE_VIEW:
      return (
        <ReminderRecurrence
          reminderData={reminderData}
          setReminderData={setReminderData}
          setViewAction={setViewAction}
          reminderReadMode={reminderReadMode}
          refreshToTime={refreshToTime}
          setRefreshToTime={setRefreshToTime}
        />
      );
    default:
      return <div>not having this action view Right now</div>;
  }
};

export default ReminderForm;
